import { useState, useEffect, useCallback, useRef } from "react";
import {
  getPermisosUsuarioService,
  asignarPermisosService,
  actualizarPermisosService,
  eliminarPermisosService,
  getPermisosUsuarioActualService,
  getAllPermisosService,
} from "modules/copropiedades/services/permisosService";
import { getPropiedadesService } from "modules/copropiedades/services/propiedades";
import { getUserCommerces, getCurrentUser } from "modules/copropiedades/services/authService";

export const usePermisos = () => {
  const [usuarios, setUsuarios] = useState([]);
  const [propiedades, setPropiedades] = useState([]);
  const [permisos, setPermisos] = useState({});
  const [permisosData, setPermisosData] = useState([]);
  const [usuarioSeleccionado, setUsuarioSeleccionado] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [todosLosPermisos, setTodosLosPermisos] = useState([]);
  const [loadingAllPermisos, setLoadingAllPermisos] = useState(false);

  const isMounted = useRef(true);

  const MODULOS = [
    {
      id: "propiedades",
      nombre: "Propiedades",
      icono: "🏢",
      acciones: ["ver", "crear", "editar", "activar", "desactivar"],
    },
    {
      id: "usuarios",
      nombre: "Usuarios",
      icono: "👤",
      acciones: ["ver", "crear", "editar", "eliminar"],
    },
    { id: "registros", nombre: "Registros", icono: "🚗", acciones: ["ver", "entrada", "salida"] },
  ];

  const loadPropiedades = useCallback(async () => {
    try {
      const result = await getPropiedadesService();
      if (result.success && isMounted.current) {
        setPropiedades(result.data || []);
      }
    } catch (err) {
      console.error("Error al cargar propiedades:", err);
    }
  }, []);

  const loadPermisosUsuario = useCallback(async (userId) => {
    if (!userId) return {};
    setLoading(true);
    setError(null);
    try {
      const result = await getPermisosUsuarioService(userId);
      if (result.success && isMounted.current) {
        const permisosMap = {};
        const dataArray = Array.isArray(result.data) ? result.data : [];
        setPermisosData(dataArray);
        dataArray.forEach((permiso) => {
          const key = `${permiso.co_prop_id}_${permiso.co_table}`;
          permisosMap[key] = { rol: permiso.rol || {}, id: permiso.id };
        });
        setPermisos(permisosMap);
        return permisosMap;
      } else {
        if (isMounted.current) {
          setPermisosData([]);
          setPermisos({});
        }
        return {};
      }
    } catch (err) {
      console.error("Error al cargar permisos del usuario:", err);
      if (isMounted.current) {
        setError(err.message);
        setPermisosData([]);
        setPermisos({});
      }
      return {};
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  }, []);

  const loadAllPermisos = useCallback(async () => {
    setLoadingAllPermisos(true);
    setError(null);
    try {
      const result = await getAllPermisosService();
      if (result.success && isMounted.current) {
        setTodosLosPermisos(result.data || []);
        return result.data || [];
      } else {
        if (isMounted.current) setError(result.error);
        return [];
      }
    } catch (err) {
      if (isMounted.current) setError(err.message);
      return [];
    } finally {
      if (isMounted.current) setLoadingAllPermisos(false);
    }
  }, []);

  const asignarPermisos = useCallback(
    async (userId, permisosData) => {
      setIsSubmitting(true);
      setError(null);
      try {
        const result = await asignarPermisosService(userId, permisosData);
        if (result.success) {
          await loadPermisosUsuario(userId);
          await loadAllPermisos();
          return { success: true, message: result.message };
        } else {
          setError(result.error);
          return { success: false, error: result.error };
        }
      } catch (err) {
        setError(err.message);
        return { success: false, error: err.message };
      } finally {
        setIsSubmitting(false);
      }
    },
    [loadPermisosUsuario, loadAllPermisos]
  );

  const actualizarPermisos = useCallback(
    async (permisoId, userId, coPropId, table, rol) => {
      setIsSubmitting(true);
      setError(null);
      try {
        const result = await actualizarPermisosService(permisoId, userId, coPropId, table, rol);
        if (result.success) {
          await loadPermisosUsuario(userId);
          await loadAllPermisos();
          return { success: true, message: result.message };
        } else {
          setError(result.error);
          return { success: false, error: result.error };
        }
      } catch (err) {
        setError(err.message);
        return { success: false, error: err.message };
      } finally {
        setIsSubmitting(false);
      }
    },
    [loadPermisosUsuario, loadAllPermisos]
  );

  // 🔥 NUEVO: Acepta permisosAEliminar
  const guardarPermisos = useCallback(
    async (userId, permisosData, permisosAEliminar = []) => {
      setIsSubmitting(true);
      setError(null);
      try {
        const results = [];

        // 🔥 1. Eliminar permisos antiguos de propiedades (con co_prop_id !== 0)
        for (const permisoAntiguo of permisosAEliminar) {
          if (permisoAntiguo.id && permisoAntiguo.table === "propiedades") {
            try {
              const deleteResult = await eliminarPermisosService(
                userId,
                permisoAntiguo.co_prop_id,
                permisoAntiguo.table
              );
            } catch (deleteError) {
              console.error("❌ Error al eliminar permiso antiguo:", deleteError);
            }
          }
        }

        // 🔥 2. Crear o actualizar permisos
        for (const permiso of permisosData) {
          const key = `${permiso.co_prop_id}_${permiso.table}`;
          const existing = permisos[key];

          let result;
          // 🔥 Si el permiso trae un id, es una actualización (PUT)
          if (permiso.id) {
            result = await actualizarPermisosService(
              permiso.id,
              userId,
              permiso.co_prop_id,
              permiso.table,
              permiso.rol
            );
          } else if (existing && existing.id) {
            // Si existe en el estado pero no trajo id, actualizar
            result = await actualizarPermisosService(
              existing.id,
              userId,
              permiso.co_prop_id,
              permiso.table,
              permiso.rol
            );
          } else {
            // Crear nuevo permiso
            result = await asignarPermisosService(userId, [permiso]);
            if (result.success && result.data && result.data.length > 0) {
              result = { success: true, data: result.data[0] };
            }
          }
          results.push(result);
        }

        const allSuccess = results.every((r) => r.success);
        const errors = results.filter((r) => !r.success).map((r) => r.error);

        if (allSuccess) {
          await loadPermisosUsuario(userId);
          await loadAllPermisos();

          // Refrescar localStorage si es el usuario actual
          try {
            const currentUser = getCurrentUser();
            if (Number(currentUser?.id) === Number(userId)) {
              await getUserCommerces();
            }
            // Notificar a los módulos suscritos
            window.dispatchEvent(new CustomEvent("permissionsUpdated", { detail: { userId } }));
          } catch (refreshError) {
            console.error("❌ Error al refrescar permisos:", refreshError);
          }

          return { success: true, message: "Permisos guardados exitosamente" };
        } else {
          return { success: false, error: errors.join(", ") };
        }
      } catch (err) {
        setError(err.message);
        return { success: false, error: err.message };
      } finally {
        setIsSubmitting(false);
      }
    },
    [permisos, loadPermisosUsuario, loadAllPermisos]
  );

  const eliminarPermisos = useCallback(
    async (userId, coPropId, table) => {
      setIsSubmitting(true);
      setError(null);
      try {
        const result = await eliminarPermisosService(userId, coPropId, table);
        if (result.success) {
          await loadPermisosUsuario(userId);
          await loadAllPermisos();
          return { success: true, message: result.message };
        } else {
          setError(result.error);
          return { success: false, error: result.error };
        }
      } catch (err) {
        setError(err.message);
        return { success: false, error: err.message };
      } finally {
        setIsSubmitting(false);
      }
    },
    [loadPermisosUsuario, loadAllPermisos]
  );

  const getPermisosUsuarioActual = useCallback(async () => {
    try {
      const result = await getPermisosUsuarioActualService();
      if (result.success) return result.data;
      return [];
    } catch (err) {
      console.error("Error al obtener permisos del usuario actual:", err);
      return [];
    }
  }, []);

  useEffect(() => {
    if (isMounted.current) {
      loadPropiedades();
      loadAllPermisos();
    }
    return () => {
      isMounted.current = false;
    };
  }, [loadPropiedades, loadAllPermisos]);

  return {
    usuarios,
    propiedades,
    permisos,
    permisosData,
    usuarioSeleccionado,
    loading,
    error,
    isSubmitting,
    MODULOS,
    todosLosPermisos,
    loadingAllPermisos,
    loadAllPermisos,
    setUsuarios,
    setUsuarioSeleccionado,
    loadPermisosUsuario,
    asignarPermisos,
    actualizarPermisos,
    guardarPermisos,
    eliminarPermisos,
    getPermisosUsuarioActual,
    loadPropiedades,
  };
};
